import React from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const mockTokens = [
    { symbol: 'ETH', name: 'Ethereum', amount: 2.5, value: 9000 },
    { symbol: 'USDC', name: 'USD Coin', amount: 3000, value: 3000 },
    { symbol: 'STRK', name: 'StarkNet', amount: 1200, value: 1800 },
];

const mockPositions = [
    {
        protocol: 'Ekubo',
        type: 'AMM LP',
        asset: 'ETH/USDC',
        apy: 12.5,
        profit: 320,
        value: 5000,
        health: null,
    },
    {
        protocol: 'Vesu',
        type: 'Lending',
        asset: 'ETH',
        apy: 4.2,
        profit: 120,
        value: 4000,
        health: 2.1,
    },
    {
        protocol: 'Vesu',
        type: 'Borrowing',
        asset: 'USDC',
        apy: 7.8,
        profit: -30,
        value: 1000,
        health: 1.4,
    },
    {
        protocol: 'extended',
        type: 'Perps',
        asset: 'ETH',
        apy: -10.5,
        profit: -250,
        value: 3500,
        health: null,
    }
];

const COLORS = ['#06b6d4', '#818cf8', '#f472b6', '#facc15', '#34d399'];

function PortfolioPieChart({ tokens }: { tokens: typeof mockTokens }) {
    return (
        <ResponsiveContainer width={350} height={350}>
            <PieChart>
                <Pie
                    data={tokens}
                    dataKey="value"
                    nameKey="symbol"
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                    {tokens.map((entry, idx) => (
                        <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
                    ))}
                </Pie>
                <Tooltip formatter={(value: number) => `$${value.toLocaleString()}`} />
                <Legend />
            </PieChart>
        </ResponsiveContainer>
    );
}

const Portfolio: React.FC = () => {
    return (
        <div className="p-6 text-white">
            {/* Overview Section */}
            <h1 className="text-3xl font-bold mb-6">Portfolio Overview</h1>
            <div className="flex flex-col md:flex-row gap-8 mb-10">
                {/* Token List */}
                <div className="flex-1 bg-gray-800 rounded-lg p-6">
                    <h2 className="text-xl font-semibold mb-4">Tokens Held</h2>
                    <table className="w-full text-left">
                        <thead>
                            <tr className="text-gray-400">
                                <th className="py-2">Token</th>
                                <th className="py-2">Amount</th>
                                <th className="py-2">Value ($)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {mockTokens.map((token) => (
                                <tr key={token.symbol} className="border-t border-gray-700">
                                    <td className="py-2 font-medium">{token.symbol} <span className="text-gray-400 text-sm">{token.name}</span></td>
                                    <td className="py-2">{token.amount}</td>
                                    <td className="py-2">${token.value.toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {/* Pie Chart */}
                <div className="flex items-center justify-center flex-1">
                    <PortfolioPieChart tokens={mockTokens} />
                </div>
            </div>

            {/* DeFi Positions Section */}
            <h2 className="text-2xl font-bold mb-4">DeFi Positions</h2>
            <div className="overflow-x-auto">
                <table className="w-full bg-gray-800 rounded-lg">
                    <thead>
                        <tr className="text-gray-400">
                            <th className="py-3 px-4">Protocol</th>
                            <th className="py-3 px-4">Type</th>
                            <th className="py-3 px-4">Asset</th>
                            <th className="py-3 px-4">APY</th>
                            <th className="py-3 px-4">Profit ($)</th>
                            <th className="py-3 px-4">Value ($)</th>
                            <th className="py-3 px-4">Health Factor</th>
                        </tr>
                    </thead>
                    <tbody>
                        {mockPositions.map((pos, i) => (
                            <tr key={i} className="border-t border-gray-700">
                                <td className="py-2 px-4 font-medium">{pos.protocol}</td>
                                <td className="py-2 px-4">{pos.type}</td>
                                <td className="py-2 px-4">{pos.asset}</td>
                                <td className="py-2 px-4">{pos.apy}%</td>
                                <td className={`py-2 px-4 ${pos.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>{pos.profit >= 0 ? '+' : ''}{pos.profit}</td>
                                <td className="py-2 px-4">${pos.value.toLocaleString()}</td>
                                <td className="py-2 px-4">{pos.health !== null ? pos.health : '-'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {/* Add more sections for protocol exposure, risk, etc. here */}
        </div>
    );
};

export default Portfolio; 